<script lang="ts">
  import Button from '../common/Button.svelte';
  import FileUpload from '../FileUpload.svelte';
  import AccountBreakdown from './AccountBreakdown.svelte';
  import RecurringRuleSuggestions from './RecurringRuleSuggestions.svelte';
  import EmptyState from '../common/EmptyState.svelte';
  import FormSelect from '../common/FormSelect.svelte';
  import ConfirmDialog from '../common/ConfirmDialog.svelte';
  import ImportedFilesTable from './ImportedFilesTable.svelte';
  import { parseCSV } from '../../lib/parsers/genericParser';
  import { createCategorizationPipeline, applyCategorizationToTransactions } from '../../lib/uploadService';
  import {
    getTransactions,
    getCategories,
    getRules,
    saveTransactionsForFile,
    markFileAsProcessed,
    isFileProcessed,
    getProcessedFiles,
    deleteTransactionsFromFile,
    deleteAllRules,
    deleteAllBudgets,
    getStorageProvider
  } from '../../lib/storage';
  let {
    onUploadComplete,
    onClearAllData
  }: {
    onUploadComplete: () => void;
    onClearAllData: () => void;
  } = $props();

  let processedFiles = $state<Array<{ filename: string; transactionCount: number; processedAt: string }>>([]);
  let selectedFile = $state<string>('');
  let testDataLoading = $state(false);
  let testDataMessage = $state('');
  let testDataDeleting = $state(false);
  let testDataLoaded = $state(false);
  let deleteFileStatus = $state<string>('');
  let deleteRulesStatus = $state<string>('');
  let deleteBudgetsStatus = $state<string>('');
  let exportStatus = $state<string>('');
  let isExporting = $state(false);
  let importStatus = $state<string>('');
  let isImporting = $state(false);
  let fileInputRef: HTMLInputElement;

  // Confirmation dialog state
  let showConfirmDeleteFile = $state(false);
  let showConfirmDeleteRules = $state(false);
  let showConfirmDeleteBudgets = $state(false);
  let showConfirmClearAll = $state(false);
  let showConfirmImport = $state(false);
  let pendingImportFile = $state<File | null>(null);

  let accountBreakdownRef: any;
  let fileUploadRef: any;
  let folderName = $state<string>('');
  let folderError = $state<string>('');
  let folderImporting = $state<boolean>(false);

  async function handleUploadComplete() {
    onUploadComplete();
    // Reload account breakdown and processed files after upload
    if (accountBreakdownRef?.reload) {
      await accountBreakdownRef.reload();
    }
    await loadProcessedFiles();
  }

  async function handleLoadTestData() {
    testDataLoading = true;
    testDataMessage = '';

    try {
      const { importTestData } = await import('../../lib/testDataGenerator');
      const result = await importTestData();

      if (result.success) {
        testDataMessage = `✅ Successfully imported ${result.count} test transactions!`;
        testDataLoaded = true;
        await handleUploadComplete();
      } else {
        testDataMessage = `❌ Error: ${result.error}`;
      }
    } catch (error) {
      console.error('Error loading test data:', error);
      testDataMessage = `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      testDataLoading = false;

      // Clear message after 5 seconds
      setTimeout(() => {
        testDataMessage = '';
      }, 5000);
    }
  }

  async function handleDeleteTestData() {
    testDataDeleting = true;
    testDataMessage = '';

    try {
      const { deleteTestData } = await import('../../lib/testDataGenerator');
      const result = await deleteTestData();

      if (result.success) {
        testDataMessage = `✅ Successfully deleted ${result.count} test transactions!`;
        testDataLoaded = false;
        await handleUploadComplete();
      } else {
        testDataMessage = `❌ Error: ${result.error}`;
      }
    } catch (error) {
      console.error('Error deleting test data:', error);
      testDataMessage = `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      testDataDeleting = false;

      // Clear message after 5 seconds
      setTimeout(() => {
        testDataMessage = '';
      }, 5000);
    }
  }

  async function loadProcessedFiles() {
    processedFiles = await getProcessedFiles();
  }

  function promptDeleteFromFile() {
    if (!selectedFile) return;
    showConfirmDeleteFile = true;
  }

  async function confirmDeleteFromFile() {
    if (!selectedFile) return;

    deleteFileStatus = 'Deleting transactions...';
    const result = await deleteTransactionsFromFile(selectedFile);

    if (result.success) {
      deleteFileStatus = `Deleted ${result.deletedCount} transaction(s) from ${selectedFile}`;
      selectedFile = '';
      await loadProcessedFiles();
      onUploadComplete(); // Refresh the main view
    } else {
      deleteFileStatus = `Error: ${result.error || 'Failed to delete transactions'}`;
    }

    setTimeout(() => {
      deleteFileStatus = '';
    }, 5000);
  }

  function promptDeleteAllRules() {
    showConfirmDeleteRules = true;
  }

  async function confirmDeleteAllRules() {
    deleteRulesStatus = 'Deleting all rules...';
    const success = await deleteAllRules();

    if (success) {
      deleteRulesStatus = 'All rules deleted successfully';
      onUploadComplete(); // Refresh the main view
    } else {
      deleteRulesStatus = 'Error: Failed to delete all rules';
    }

    setTimeout(() => {
      deleteRulesStatus = '';
    }, 5000);
  }

  function promptDeleteAllBudgets() {
    showConfirmDeleteBudgets = true;
  }

  async function confirmDeleteAllBudgets() {
    deleteBudgetsStatus = 'Deleting all budgets...';
    const success = await deleteAllBudgets();

    if (success) {
      deleteBudgetsStatus = 'All budgets deleted successfully';
      onUploadComplete(); // Refresh the main view
    } else {
      deleteBudgetsStatus = 'Error: Failed to delete all budgets';
    }

    setTimeout(() => {
      deleteBudgetsStatus = '';
    }, 5000);
  }

  function promptClearAllData() {
    showConfirmClearAll = true;
  }

  function confirmClearAllData() {
    onClearAllData();
  }

  async function handleExportDatabase() {
    isExporting = true;
    exportStatus = '';

    try {
      const provider = getStorageProvider();

      // Check if provider supports exportData
      if (!provider.exportData) {
        exportStatus = '❌ Export not supported for current storage provider';
        return;
      }

      // Export database as blob
      const blob = await provider.exportData();

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-backup-${new Date().toISOString().split('T')[0]}.db`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      exportStatus = '✅ Database exported successfully!';
    } catch (error) {
      console.error('Error exporting database:', error);
      exportStatus = `❌ Error: ${error instanceof Error ? error.message : 'Failed to export database'}`;
    } finally {
      isExporting = false;

      // Clear message after 5 seconds
      setTimeout(() => {
        exportStatus = '';
      }, 5000);
    }
  }

  function handleImportClick() {
    fileInputRef?.click();
  }

  function handleFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Check file extension
    if (!file.name.endsWith('.db') && !file.name.endsWith('.sqlite') && !file.name.endsWith('.sqlite3')) {
      importStatus = '❌ Please select a valid SQLite database file (.db, .sqlite, or .sqlite3)';
      setTimeout(() => {
        importStatus = '';
      }, 5000);
      return;
    }

    // Store file and show confirmation dialog
    pendingImportFile = file;
    showConfirmImport = true;
  }

  async function confirmImportDatabase() {
    if (!pendingImportFile) return;

    isImporting = true;
    importStatus = '';

    try {
      const provider = getStorageProvider();

      // Check if provider supports importData
      if (!provider.importData) {
        importStatus = '❌ Import not supported for current storage provider';
        return;
      }

      // Read file as ArrayBuffer
      const arrayBuffer = await pendingImportFile.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);

      // Validate it's a SQLite database by checking magic bytes
      const magicBytes = Array.from(data.slice(0, 16));
      const sqliteHeader = [83, 81, 76, 105, 116, 101, 32, 102, 111, 114, 109, 97, 116, 32, 51, 0]; // "SQLite format 3\0"

      if (JSON.stringify(magicBytes) !== JSON.stringify(sqliteHeader)) {
        importStatus = '❌ Invalid SQLite database file';
        return;
      }

      // Import the database
      await provider.importData(data);

      // Run migrations to update schema if needed
      const { runMigrations, needsMigrations } = await import('../../lib/migrations');

      if (needsMigrations()) {
        importStatus = '⏳ Applying schema migrations...';
        await runMigrations();

        // Save database after migrations
        const { saveDB } = await import('../../lib/db');
        await saveDB();
      }

      importStatus = '✅ Database imported successfully! Reloading...';

      // Wait a moment to show the success message
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Error importing database:', error);
      importStatus = `❌ Error: ${error instanceof Error ? error.message : 'Failed to import database'}`;

      // Clear message after 5 seconds on error
      setTimeout(() => {
        importStatus = '';
      }, 5000);
    } finally {
      isImporting = false;
      pendingImportFile = null;

      // Reset file input
      if (fileInputRef) {
        fileInputRef.value = '';
      }
    }
  }

  // Load processed files on mount and check for test data
  $effect(() => {
    loadProcessedFiles().then(() => {
      // Check if test data exists
      const hasTestData = processedFiles.some(f =>
        f.filename === 'test-credit-card.csv' || f.filename === 'test-checking.csv'
      );
      testDataLoaded = hasTestData;
    });
  });

  // Check if user has imported their own data (non-test files)
  let hasRealData = $derived(processedFiles.some(f =>
    f.filename !== 'test-credit-card.csv' && f.filename !== 'test-checking.csv'
  ));

  async function handleSelectFolder() {
    folderError = '';

    // Check if File System Access API is supported
    if (!('showDirectoryPicker' in window)) {
      folderError = 'Folder selection is not supported in your browser. Please use Chrome, Edge, or another Chromium-based browser.';
      return;
    }

    try {
      // @ts-ignore - File System Access API
      const dirHandle = await window.showDirectoryPicker({
        mode: 'read',
        startIn: 'documents'
      });

      folderName = dirHandle.name;

      // Recursively read all CSV files from the folder and subfolders
      const csvFilesWithPaths: Array<{ file: File; path: string[] }> = [];
      await scanDirectoryRecursively(dirHandle, csvFilesWithPaths);

      if (csvFilesWithPaths.length === 0) {
        folderError = `No CSV files found in "${folderName}" or its subfolders`;
        return;
      }

      // Process files using existing upload logic
      console.log(`Found ${csvFilesWithPaths.length} CSV files in folder "${folderName}" and subfolders`);

      // Import all found CSV files with metadata
      folderImporting = true;
      folderError = `Importing ${csvFilesWithPaths.length} CSV files...`;

      try {
        // Import required utilities
        const { hashFile } = await import('../../lib/utils/fileHash');
        const { isFileHashProcessed } = await import('../../lib/storage');

        // Create categorization pipeline
        const pipeline = createCategorizationPipeline();

        let allNewTransactions = [];
        let processedCount = 0;
        let skippedCount = 0;
        let totalDuplicates = 0;

        // Process each file with its path metadata
        for (const { file, path } of csvFilesWithPaths) {
          // Hash the file for duplicate detection
          const fileHash = await hashFile(file);

          // Check if file content already processed (by hash, not filename)
          const hashCheck = await isFileHashProcessed(fileHash);
          if (hashCheck.processed) {
            console.log(`${file.name} content already processed as "${hashCheck.filename}", skipping`);
            skippedCount++;
            continue;
          }

          // Extract bank/person/accountType from folder path
          const { bank, person, accountType } = extractMetadataFromPath(path);
          console.log(`Processing ${file.name}: bank=${bank}, person=${person}, accountType=${accountType}`);

          // Parse CSV
          const text = await file.text();
          let transactions = parseCSV(text, file.name, bank);

          // Build person field from person and accountType for transactions
          let personValue = person;
          if (personValue && accountType) {
            personValue = `${personValue} - ${accountType}`;
          }

          // Apply bank/person metadata to all transactions
          transactions = transactions.map(t => ({
            ...t,
            bank: t.bank || bank, // Use detected bank, fallback to path
            person: t.person || personValue
          }));

          // Apply categorization
          transactions = await applyCategorizationToTransactions(transactions, pipeline);

          // Save transactions
          if (transactions.length > 0) {
            const saveResult = await saveTransactionsForFile(file.name, transactions);
            if (saveResult) {
              const duplicatesSkipped = typeof saveResult === 'object' ? saveResult.duplicatesSkipped : 0;
              const savedCount = typeof saveResult === 'object' ? saveResult.saved || saveResult.count : transactions.length;

              // Always track duplicates, even if nothing was saved
              totalDuplicates += duplicatesSkipped;

              if (savedCount > 0) {
                // Mark file as processed with hash and metadata (separately stored)
                await markFileAsProcessed(file.name, savedCount, fileHash, { bank, person, accountType });
                allNewTransactions = [...allNewTransactions, ...transactions.slice(0, savedCount)];
                processedCount++;
              }
            }
          }
        }

        if (allNewTransactions.length > 0) {
          folderError = '';
          let message = `✅ Imported ${allNewTransactions.length} transactions from ${processedCount} file(s)`;
          if (totalDuplicates > 0) {
            message += ` (${totalDuplicates} duplicates skipped)`;
          }
          if (skippedCount > 0) {
            message += ` • ${skippedCount} file(s) already processed`;
          }
          folderName = message;

          // Reload data
          await handleUploadComplete();
        } else if (skippedCount > 0) {
          folderError = '';
          folderName = `All ${skippedCount} file(s) already processed`;
        } else if (totalDuplicates > 0) {
          folderError = '';
          folderName = `All ${totalDuplicates} transaction(s) from ${csvFilesWithPaths.length} file(s) are duplicates (already imported)`;
        } else {
          folderError = 'No valid transactions found in files';
        }
      } catch (importErr) {
        console.error('Error importing files:', importErr);
        folderError = `Import failed: ${importErr.message}`;
      } finally {
        folderImporting = false;
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled the picker
        return;
      }
      console.error('Error selecting folder:', err);
      folderError = `Error: ${err.message}`;
    }
  }

  /**
   * Recursively scan a directory and all subdirectories for CSV files
   * Captures path structure to extract bank/person metadata
   */
  async function scanDirectoryRecursively(
    dirHandle: any,
    csvFiles: Array<{ file: File; path: string[] }>,
    currentPath: string[] = []
  ): Promise<void> {
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        // If it's a CSV file, add it to the list with its path
        if (entry.name.toLowerCase().endsWith('.csv')) {
          const file = await entry.getFile();
          csvFiles.push({ file, path: [...currentPath] });
          console.log(`Found CSV: ${[...currentPath, entry.name].join('/')}`);
        }
      } else if (entry.kind === 'directory') {
        // If it's a directory, recursively scan it
        console.log(`Scanning subdirectory: ${entry.name}`);
        await scanDirectoryRecursively(entry, csvFiles, [...currentPath, entry.name]);
      }
    }
  }

  /**
   * Extract bank, person, and accountType from folder path
   * Supports patterns like:
   * - bank/person/*.csv -> { bank, person }
   * - bank/person - accountType/*.csv -> { bank, person, accountType }
   * - bank/*.csv -> { bank }
   */
  function extractMetadataFromPath(pathSegments: string[]): { bank?: string; person?: string; accountType?: string } {
    const knownBanks = ['amex', 'bmo', 'chase', 'fidelity', 'sofi', 'usbank', 'us bank', 'wellsfargo', 'wells fargo', 'bofa', 'bankofamerica'];

    let bank: string | undefined;
    let person: string | undefined;
    let accountType: string | undefined;

    // Look through path segments to find bank and person
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i].toLowerCase();

      // Check if this segment matches a known bank
      if (knownBanks.some(b => segment.includes(b))) {
        bank = pathSegments[i]; // Use original casing

        // Next segment (if exists) might be "person" or "person - accountType"
        if (i + 1 < pathSegments.length) {
          const personSegment = pathSegments[i + 1];

          // Check if it contains " - " separator (e.g., "Shelly - Credit Card")
          if (personSegment.includes(' - ')) {
            const parts = personSegment.split(' - ');
            person = parts[0].trim();
            accountType = parts.slice(1).join(' - ').trim(); // Handle multiple dashes
          } else {
            person = personSegment;
          }
        }
        break;
      }
    }

    return { bank, person, accountType };
  }
</script>

<div class="data-tab">
  <!-- Import Section (Top) -->
  <div class="import-section">
    <h3>Import & Restore</h3>

    <!-- Upload CSV Files -->
    <div class="import-item">
      <div class="import-content">
        <div class="import-info">
          <h4>Upload CSV Files</h4>
          <p>Supports multiple files from Chase, Capital One, Amex, etc.</p>
        </div>
        <div class="import-action">
          <FileUpload onUpload={handleUploadComplete} />
        </div>
      </div>
    </div>

    <!-- Bulk Import from Folder -->
    <div class="import-item">
      <div class="import-content">
        <div class="import-info">
          <h4>Bulk Import from Folder</h4>
          <p>Select a folder containing CSV files to import them all at once.</p>
        </div>
        <div class="import-action">
          <Button variant="secondary" onclick={handleSelectFolder} disabled={folderImporting}>
            {folderImporting ? '⏳ Importing...' : '📁 Select Folder'}
          </Button>
        </div>
      </div>
      {#if folderName}
        <div class="folder-info">
          {folderName}
        </div>
      {/if}
      {#if folderError}
        <div class="folder-status" class:error={!folderImporting} class:importing={folderImporting}>
          {folderError}
        </div>
      {/if}
    </div>

    <!-- Test Data -->
    {#if !hasRealData}
      <div class="import-item">
        <div class="import-content">
          <div class="import-info">
            <h4>Try with Test Data</h4>
            <p>Load sample transactions to explore features without uploading your own data.</p>
          </div>
          <div class="import-action">
            <Button variant="primary" onclick={handleLoadTestData} disabled={testDataLoading || testDataDeleting || testDataLoaded}>
              {testDataLoading ? '⏳ Loading...' : testDataLoaded ? '✓ Test Data Loaded' : '🎲 Load Test Data'}
            </Button>
            {#if testDataLoaded}
              <Button variant="warning" onclick={handleDeleteTestData} disabled={testDataLoading || testDataDeleting}>
                {testDataDeleting ? '⏳ Deleting...' : '🗑️ Delete Test Data'}
              </Button>
            {/if}
          </div>
        </div>
        {#if testDataMessage}
          <div class="test-data-message" class:success={testDataMessage.includes('✅')} class:error={testDataMessage.includes('❌')}>
            {testDataMessage}
          </div>
        {/if}
      </div>
    {/if}

    <!-- Backup & Restore -->
    <div class="import-item">
      <div class="import-content">
        <div class="import-info">
          <h4>Backup & Restore</h4>
          <p>Export your database as a SQLite file for backup, or import a previously exported database.</p>
        </div>
        <div class="import-action">
          <Button variant="secondary" onclick={handleExportDatabase} disabled={isExporting || isImporting}>
            {isExporting ? '⏳ Exporting...' : '💾 Export'}
          </Button>
          <Button variant="secondary" onclick={handleImportClick} disabled={isImporting || isExporting}>
            {isImporting ? '⏳ Importing...' : '📥 Import'}
          </Button>
        </div>
      </div>
      {#if exportStatus}
        <div class="status-message" class:success={exportStatus.includes('✅')} class:error={exportStatus.startsWith('❌')}>
          {exportStatus}
        </div>
      {/if}
      {#if importStatus}
        <div class="status-message" class:success={importStatus.includes('✅')} class:error={importStatus.startsWith('❌')}>
          {importStatus}
        </div>
      {/if}
    </div>

    <!-- Hidden file input for database import -->
    <input
      type="file"
      accept=".db,.sqlite,.sqlite3"
      bind:this={fileInputRef}
      onchange={handleFileSelected}
      style="display: none;"
    />
  </div>

  <!-- Imported Files Table (Middle) -->
  <ImportedFilesTable onUpdate={handleUploadComplete} />

  <!-- Delete Section (Bottom) -->
  <div class="delete-section">
    <h3>Delete & Cleanup</h3>

    <div class="delete-grid">
      <div class="delete-card">
        <h4>Delete Transactions from CSV File</h4>
        <p class="card-description">Remove all transactions imported from a specific file.</p>
        <div class="compact-controls">
          <FormSelect
            label="Select File"
            bind:value={selectedFile}
            options={processedFiles.map(f => ({ value: f.filename, label: `${f.filename} (${f.transactionCount} transactions)` }))}
            placeholder="Choose a file..."
          />
          <Button variant="warning" onclick={promptDeleteFromFile} disabled={!selectedFile}>
            Delete from File
          </Button>
        </div>
        {#if deleteFileStatus}
          <div class="status-message" class:error={deleteFileStatus.startsWith('Error')}>
            {deleteFileStatus}
          </div>
        {/if}
      </div>

      <div class="delete-card">
        <div class="card-header">
          <div>
            <h4>Delete All Rules</h4>
            <p class="card-description">Remove all categorization rules.</p>
          </div>
          <Button variant="warning" onclick={promptDeleteAllRules}>
            Delete All Rules
          </Button>
        </div>
        {#if deleteRulesStatus}
          <div class="status-message" class:error={deleteRulesStatus.startsWith('Error')}>
            {deleteRulesStatus}
          </div>
        {/if}
      </div>

      <div class="delete-card">
        <div class="card-header">
          <div>
            <h4>Delete All Budgets</h4>
            <p class="card-description">Remove all budget configurations.</p>
          </div>
          <Button variant="warning" onclick={promptDeleteAllBudgets}>
            Delete All Budgets
          </Button>
        </div>
        {#if deleteBudgetsStatus}
          <div class="status-message" class:error={deleteBudgetsStatus.startsWith('Error')}>
            {deleteBudgetsStatus}
          </div>
        {/if}
      </div>

      <div class="delete-card danger">
        <div class="card-header">
          <div>
            <h4>Clear All Data</h4>
            <p class="card-description">Delete all data and reset the application.</p>
          </div>
          <Button variant="danger" onclick={promptClearAllData}>
            Clear All Data
          </Button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Confirmation Dialogs -->
<ConfirmDialog
  isOpen={showConfirmDeleteFile}
  onClose={() => showConfirmDeleteFile = false}
  onConfirm={confirmDeleteFromFile}
  title="Delete Transactions from File"
  message={`Are you sure you want to delete all transactions imported from "${selectedFile}"?

This action cannot be undone.`}
  confirmText="Delete"
  variant="warning"
/>

<ConfirmDialog
  isOpen={showConfirmDeleteRules}
  onClose={() => showConfirmDeleteRules = false}
  onConfirm={confirmDeleteAllRules}
  title="Delete All Rules"
  message={`Are you sure you want to delete all categorization rules?

Existing transaction categories will not be affected, but future transactions will not be automatically categorized.

This action cannot be undone.`}
  confirmText="Delete All Rules"
  variant="warning"
/>

<ConfirmDialog
  isOpen={showConfirmDeleteBudgets}
  onClose={() => showConfirmDeleteBudgets = false}
  onConfirm={confirmDeleteAllBudgets}
  title="Delete All Budgets"
  message={`Are you sure you want to delete all budget configurations?

Your transactions will not be affected.

This action cannot be undone.`}
  confirmText="Delete All Budgets"
  variant="warning"
/>

<ConfirmDialog
  isOpen={showConfirmClearAll}
  onClose={() => showConfirmClearAll = false}
  onConfirm={confirmClearAllData}
  title="Clear All Data"
  message={`Are you sure you want to clear ALL data?

This will delete:
• All transactions
• All rules
• All budgets
• All settings

This action cannot be undone and will reset the application to its initial state.`}
  confirmText="Clear All Data"
  variant="danger"
/>

<ConfirmDialog
  isOpen={showConfirmImport}
  onClose={() => {
    showConfirmImport = false;
    pendingImportFile = null;
    if (fileInputRef) fileInputRef.value = '';
  }}
  onConfirm={confirmImportDatabase}
  title="Import Database"
  message={`Are you sure you want to import "${pendingImportFile?.name}"?

⚠️ WARNING: This will REPLACE all your current data with the data from this file.

Before importing:
• Make sure you have a backup of your current data
• Verify this is the correct database file

The import will:
• Replace all transactions, rules, budgets, and settings
• Automatically apply any missing database migrations
• Reload the application after import

This action cannot be undone.`}
  confirmText="Import Database"
  variant="danger"
/>

<style lang="scss">
  .data-tab {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2xl);

    h3 {
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-lg) 0;
    }

    h4 {
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-sm) 0;
    }

    .card-description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      margin: 0 0 var(--spacing-md) 0;
      line-height: var(--line-height-normal);
    }

    // Import Section (Top)
    .import-section {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);

      .import-item {
        padding: var(--spacing-lg);
        border: var(--border-width-thin) solid var(--color-border);
        border-radius: var(--radius-lg);
        background: var(--color-background);

        .import-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-xl);

          @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        .import-info {
          flex: 1;
          min-width: 0;

          h4 {
            margin: 0 0 var(--spacing-xs) 0;
          }

          p {
            margin: 0;
            font-size: var(--font-size-sm);
            color: var(--color-text-secondary);
          }
        }

        .import-action {
          display: flex;
          gap: var(--spacing-sm);
          flex-shrink: 0;
          align-items: center;

          @media (max-width: 768px) {
            width: 100%;
          }
        }

        .folder-info {
          margin-top: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          background: var(--color-primary-light);
          border: var(--border-width-thin) solid var(--color-primary-border);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        .folder-status {
          margin-top: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          border: var(--border-width-thin) solid;
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);

          &.error {
            background: var(--color-danger-light);
            border-color: var(--color-danger-border);
            color: var(--color-danger-dark);
          }

          &.importing {
            background: var(--color-warning-light);
            border-color: var(--color-warning-border);
            color: var(--color-warning-darker);
          }
        }

        .test-data-message,
        .status-message {
          margin-top: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          border: var(--border-width-thin) solid;
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-weight: var(--font-weight-medium);

          &.success {
            background: var(--color-success-light);
            border-color: var(--color-success-border);
            color: var(--color-success-dark);
          }

          &.error {
            background: var(--color-danger-light);
            border-color: var(--color-danger-border);
            color: var(--color-danger-dark);
          }
        }
      }
    }

    // Delete Section (Bottom)
    .delete-section {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-lg);

      .delete-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--spacing-lg);

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }
      }

      .delete-card {
        padding: var(--spacing-lg);
        border: var(--border-width-thin) solid var(--color-border);
        border-radius: var(--radius-lg);
        background: var(--color-background);

        &.danger {
          border-color: var(--color-danger-border);
          background: var(--color-danger-light);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-md);

          @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        .compact-controls {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-sm);
        }

        .status-message {
          margin-top: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          background: var(--color-success-light);
          border: var(--border-width-thin) solid var(--color-success-border);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          color: var(--color-success-dark);

          &.error {
            background: var(--color-danger-light);
            border-color: var(--color-danger-border);
            color: var(--color-danger-dark);
          }
        }
      }
    }
  }
</style>
